import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { IFilter, ILoginAccount } from '../../interfaces';
import { BaseHttpService } from '../base-http/base-http.service';
import { IImage } from '../posts/posts.type';
import {
  IAvatar,
  ICounters,
  IDevice,
  IFavouritesFile,
  ILoginData,
  IPasswordResetConfirm,
  IPasswordResetRequest,
  IUser,
  IUsersArray,
} from './user.type';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  private _http = inject(BaseHttpService);

  private readonly _adminHref = '/admin';

  private readonly _usersUrl = `/users`;

  private readonly _loginUrl = '/login';

  private readonly _logoutUrl = '/logout';

  private readonly _currentUserUrl = `${this._usersUrl}/current`;

  private readonly _userPagedUrl = `${this._usersUrl}/paged`;

  private readonly _counters = `${this._usersUrl}/state`;

  private readonly _favshop = `${this._usersUrl}/favshop`;

  private readonly _unfavshop = `${this._usersUrl}/unfavshop`;

  private readonly _favcontent = '/favcontent';

  private readonly _avatar = '/avatar';

  private readonly _block = '/block';

  private readonly _passwordResetRequestUrl = `${this._usersUrl}/password-reset/request`;

  private readonly _passwordResetConfirmUrl = `${this._usersUrl}/password-reset/confirm`;

  public login(accountData: ILoginAccount) {
    return this._http.post<ILoginData>(`${this._usersUrl}${this._loginUrl}`, accountData);
  }

  public requestPasswordReset(body: IPasswordResetRequest) {
    return this._http.post<null>(this._passwordResetRequestUrl, body);
  }

  public confirmPasswordReset(body: IPasswordResetConfirm) {
    return this._http.post<null>(this._passwordResetConfirmUrl, body);
  }

  public getCurrent(): Observable<IUser> {
    return this._http.get<IUser>(`${this._currentUserUrl}`);
  }

  public getUserById(userId: number): Observable<IUser> {
    return this._http.get<IUser>(`${this._usersUrl}/${userId}`);
  }

  public getUserByIdAsAdmin(userId: number): Observable<IUser> {
    return this._http.get<IUser>(`${this._adminHref + this._usersUrl}/${userId}`);
  }

  public getUsersPaged(filters?: IFilter): Observable<IUsersArray> {
    return this._http.get<IUsersArray>(`${this._userPagedUrl}`, filters);
  }

  public getUsersPagedAsAdmin(filters?: IFilter): Observable<IUsersArray> {
    return this._http.get<IUsersArray>(`${this._adminHref + this._userPagedUrl}`, filters);
  }

  public getCounters(): Observable<ICounters> {
    return this._http.get<ICounters>(`${this._counters}`);
  }

  public logout(): Observable<any> {
    return this._http.get<any>(`${this._usersUrl}${this._logoutUrl}`);
  }

  public getEmployees(filters?: IFilter): Observable<IUsersArray> {
    return this._http.get<IUsersArray>(`${this._userPagedUrl}`, filters);
  }

  public addShopToFavorite(shopId: number) {
    return this._http.post<null>(`${this._favshop}/${shopId}`);
  }

  public removeShopFromFavorite(shopId: number) {
    return this._http.post<null>(`${this._unfavshop}/${shopId}`);
  }

  public getFavoritesData(filters: IFilter): Observable<IFavouritesFile[]> {
    return this._http.get<IFavouritesFile[]>(`${this._usersUrl}${this._favcontent}`, filters);
  }

  public addToFavorites(id: number) {
    return this._http.post(`${this._usersUrl}${this._favcontent}/${id}`, { view: 'POST' });
  }

  public removeFromFavorites(id: number) {
    return this._http.delete(`${this._usersUrl}${this._favcontent}/${id}`);
  }

  public addAvatar(
    id: number,
    payload: { originalImage: Pick<IImage, 'id'> },
  ): Observable<IAvatar> {
    return this._http.put(`${this._usersUrl}/${id}${this._avatar}`, payload);
  }

  public addDevice(payload: IDevice): Observable<Omit<IDevice, 'type'>> {
    return this._http.post(`${this._currentUserUrl}/devices`, payload);
  }

  public deleteDevice(token: string) {
    return this._http.delete(`${this._currentUserUrl}/devices/${token}`);
  }

  public blockUser(id: number) {
    return this._http.post(`${this._usersUrl}/${id}/${this._block}`);
  }

  public updateUserDescription(user: IUser, description: string): Observable<IUser> {
    const body = JSON.parse(JSON.stringify(user)) as Record<string, unknown>;
    body['description'] = description.trim();
    for (const key of ['dateOfBirth', 'dateEmployment', 'dateDismissal'] as const) {
      if (body[key] === null || body[key] === '') {
        delete body[key];
      }
    }
    return this._http.put<IUser>(`${this._usersUrl}/${user.id}`, body);
  }
}
